import type { LinkShareTemplateData } from '~/components/communications/LinkShare';
import { DocumentSignatureRequestsResponseESignStatus } from '~/server_shared/openapi/data-contracts';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

// Placeholder for signature URL - used when document/contact not available during template processing
// Frontend replaces this placeholder with actual signing URL after document upload
export const ESIGN_SIGNATURE_URL_PLACEHOLDER = '{{SIGNATURE_URL_PLACEHOLDER}}';

// Field type constant for esign_contact dynamic fields in document templates
export const ESIGN_CONTACT_FIELD_TYPE = 'esign_contact';

export interface SignatureRequestData {
  signUrl: string;
  contact: ContactFullModel;
  claimId: number;
}

// Required<>: LinkShare declares sms_html optional, but this builder always produces all three fields
// and its callers read sms_html directly, so the weaker shape would drop that guarantee.
export const buildSignatureRequestTemplateData = ({
  signUrl,
  smsSignature,
}: {
  signUrl: string;
  smsSignature?: string;
}): Required<LinkShareTemplateData> => ({
  title_email_html: 'Signature Request',
  // No sender signature here: the email composer prepends it, and rebuilds the whole body from this
  // string when the sender is switched -- a signature baked in would render twice and go stale.
  body_email_html: `Please sign this document using the following link: <a href="${signUrl}">Signature Link</a>`,
  // LinkShare HTML-to-text converts this into the SMS card's whole message content, and the card
  // adds no signature of its own unless that content is undefined -- so this is the only SMS source.
  // It takes the organization's SMS signature, never the HTML email one: the text conversion rewrites
  // each link's visible text to its raw href, so an ordinary tracked-link email signature grows the
  // body past Twilio's 1600-character limit and the send is rejected outright.
  sms_html: `Please sign this document: ${signUrl}${smsSignature ? `\n${smsSignature}` : ''}`,
});

export const ESIGN_STATUS_CONFIG = {
  [DocumentSignatureRequestsResponseESignStatus.DRAFT]: {
    label: 'Draft',
    className: 'text-blue-700',
  },
  [DocumentSignatureRequestsResponseESignStatus.PENDING]: {
    label: 'Pending',
    className: 'text-blue-700',
  },
  [DocumentSignatureRequestsResponseESignStatus.VIEWED]: {
    label: 'Viewed',
    className: 'text-blue-700',
  },
  [DocumentSignatureRequestsResponseESignStatus.SENT]: {
    label: 'Sent',
    className: 'text-blue-700',
  },
  [DocumentSignatureRequestsResponseESignStatus.COMPLETED]: {
    label: 'Completed',
    className: 'text-green-700',
  },
  [DocumentSignatureRequestsResponseESignStatus.CANCELLED]: {
    label: 'Cancelled',
    className: 'text-red-700',
  },
  [DocumentSignatureRequestsResponseESignStatus.ERROR]: {
    label: 'Error',
    className: 'text-red-700',
  },
};
